<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ResourceList from './ResourceList.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key => t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.ALLOWLIST.${key}`);

const entries = ref([]);
const showForm = ref(false);
const phoneNumber = ref('');
const pendingDelete = ref(null);
const showDeleteModal = ref(false);

const fetchEntries = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.listAllowlistEntries(
      props.hookId
    );
    entries.value = data;
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  }
};

onMounted(fetchEntries);

const resetForm = () => {
  phoneNumber.value = '';
  showForm.value = false;
};

const createEntry = async () => {
  try {
    await MetaBusinessAgentAPI.createAllowlistEntry(
      props.hookId,
      phoneNumber.value
    );
    useAlert(T('API.CREATE_SUCCESS'));
    resetForm();
    fetchEntries();
  } catch (error) {
    useAlert(T('API.CREATE_ERROR'));
  }
};

const confirmDelete = item => {
  pendingDelete.value = item;
  showDeleteModal.value = true;
};

const deleteEntry = async () => {
  try {
    await MetaBusinessAgentAPI.deleteAllowlistEntry(
      props.hookId,
      pendingDelete.value.id
    );
    useAlert(T('API.DELETE_SUCCESS'));
    fetchEntries();
  } catch (error) {
    useAlert(T('API.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">{{ T('DESCRIPTION') }}</p>
    <div class="flex justify-end">
      <Button :label="T('ADD_BUTTON')" @click="showForm = true" />
    </div>
    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1"
    >
      <Input
        v-model="phoneNumber"
        :label="T('PHONE_NUMBER_LABEL')"
        placeholder="+15551234567"
      />
      <div class="flex justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click="resetForm"
        />
        <Button
          :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
          @click="createEntry"
        />
      </div>
    </div>
    <ResourceList
      :items="entries"
      :empty-state="T('EMPTY_STATE')"
      @delete="confirmDelete"
    >
      <template #default="{ item }">
        <p class="font-medium text-n-slate-12 m-0">
          {{ item.consumer_phone_number }}
        </p>
      </template>
    </ResourceList>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="deleteEntry"
      :title="T('DELETE_CONFIRM_TITLE')"
      :message="T('DELETE_CONFIRM_MESSAGE')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
